import { useState, useMemo, useEffect, useCallback } from 'react'
import { useHistory, generatePath } from 'react-router-dom'
import { useCurrentBrandPlan } from 'hooks/queries/brandPlans/useCurrentBrandPlan'
import { useBrandKit } from 'hooks/queries/brandKits/useBrandKit'
import { useBrandPlanSchedules } from 'hooks/queries/schedules/useBrandPlanSchedules'
import { useFontPairings } from 'hooks/queries/fontPairings'
import { useFonts } from 'hooks/queries/workspaces/useFonts'
import { deleteFont } from 'hooks/queries/workspaces/useDeleteFont'
import { useUpdateBrandVisualKit } from 'hooks/queries/workspaces/useUpdateBrandVisualKit'
import { useUpsertTextStyle } from 'hooks/queries/workspaces/brandVisualKits/useUpsertTextStyle'
import { useInfiniteBrandKitMediaFiles } from 'hooks/queries/brandKits/useInfiniteBrandKitMediaFiles'
import { useCurrentWorkspaceId } from 'hooks/useCurrentWorkspaceId'
import { useQueryClient } from 'react-query'
import { collectResults } from 'utils/query'
import { FontRole, FontStyle, BrandPlanTactic, BrandPlanScheduleContent } from 'types'
import { useBrandKitRegeneration } from 'screens/BrandKits/components/BrandKitRegenerationDrawer/hooks/useBrandKitRegeneration'
import { showToastMessage } from 'common/ToastMessage/ShowToastMessage'
import { useStyleAndFontRecommendations } from './useStyleAndFontRecommendations'
import { useContentPreferencesFields } from 'screens/BrandKits/components/ContentPreferences/useContentPreferencesFields'
import { useUpdateBrandKit } from 'hooks/queries/brandKits/useUpdateBrandKit'
import { useUpdateBrandPlan } from 'hooks/queries/useUpdateBrandPlan'
import { SCHEDULED_POSTS_PATH } from 'paths'
import { useVisualStyles } from 'hooks/queries/visualStyles'
import { useStyledImage } from 'components/VisualStylePreviewCarousel/useStyledImage'
import { StyledImageData, ContentPlanValues } from './VisualStylesForExistingUsersModal'
import dayjs from 'dayjs'

// Default style names to use for generation
const DEFAULT_STYLE_NAMES_3 = ['Morning Contrast', 'Sunset Studio', 'Black and White Minimal'] as const

export interface StepConfig {
  sequence: string[]
  triggerGenerationOnStep: string
  singleStyleGeneration?: boolean
}

export interface UseVisualStylesFlowParams {
  stepConfig: StepConfig
  onClose: () => void
}

export interface MediaFile {
  id: number
  url: string
  type: 'image'
}

export const useVisualStylesFlow = ({ stepConfig, onClose }: UseVisualStylesFlowParams) => {
  const history = useHistory()
  const queryClient = useQueryClient()
  const currentWorkspaceId = useCurrentWorkspaceId()

  // Data queries
  const { data: fontsData } = useFonts()
  const { data: fontPairingsData } = useFontPairings()
  const { data: brandPlan } = useCurrentBrandPlan()
  const { data: visualStyles } = useVisualStyles()
  const brandKitId = brandPlan?.brand_kit?.id

  const recommendations = useStyleAndFontRecommendations(brandKitId)

  const { data: brandKitData } = useBrandKit(brandKitId, { enabled: !!brandKitId })
  const brandVisualKit = brandKitData?.brand_kit?.brand_visual_kit
  const brandVisualKitId = brandVisualKit?.id
  const brandColor = brandVisualKit?.primary_color_palette?.colors?.[0]?.hex

  // State
  const [currentStep, setCurrentStep] = useState<string>(stepConfig.sequence[0])
  const [selectedMediaFileId, setSelectedMediaFileId] = useState<number | null>(null)
  const [selectedVisualStyleId, setSelectedVisualStyleId] = useState<number | null>(null)
  const [selectedFontPairingId, setSelectedFontPairingId] = useState<number | null>(null)
  const [isSaving, setIsSaving] = useState(false)
  const [additionalStyleId, setAdditionalStyleId] = useState<number | null>(null)
  const [shouldGenerateAdditionalStyle, setShouldGenerateAdditionalStyle] = useState(false)
  const [animatedImagesOptIn, setAnimatedImagesOptIn] = useState(false)
  const [contentPlanValues, setContentPlanValues] = useState<ContentPlanValues | null>(null)

  // Schedule data
  const { data: schedulesData } = useBrandPlanSchedules(
    { brandPlanId: brandPlan?.id || 0, items: 100 },
    { enabled: !!brandPlan?.id },
  )

  const plannedSchedules = useMemo(() => {
    const allSchedules = schedulesData?.pages.flatMap((page) => page.data.schedules) || []
    return allSchedules.filter((s) => s.scheduled_status === 'planned')
  }, [schedulesData])

  const currentScheduleFileIds = useMemo(() => {
    const allSchedules = schedulesData?.pages.flatMap((page) => page.data.schedules) || []
    const currentSchedule = allSchedules.find((s) => s.is_current)

    if (!currentSchedule) return []

    return currentSchedule.contents
      .filter((c: BrandPlanScheduleContent) => c.almanac_file?.key && dayjs(c.post_at).isAfter(dayjs()))
      .map((c: BrandPlanScheduleContent) => c.almanac_file!.key) // eslint-disable-line @typescript-eslint/no-non-null-assertion
  }, [schedulesData])

  const { regenerate, isLoading: isRegenerating } = useBrandKitRegeneration({
    brandPlan,
    plannedSchedules,
    shouldRegenerateTopics: false,
    fileIdsToRemove: currentScheduleFileIds,
  })

  const customFonts = useMemo(() => fontsData?.fonts ?? [], [fontsData])

  // Media files
  const mediaFilesQuery = useInfiniteBrandKitMediaFiles({ brandKitId, items: 20 })
  const rawMediaFiles = collectResults(mediaFilesQuery, (data) => data.media_files)

  const selectedSourceImage = useMemo((): MediaFile | null => {
    if (!selectedMediaFileId) return null
    const file = rawMediaFiles.find((f) => f.media_file_id === selectedMediaFileId)
    if (!file) return null
    return {
      id: file.media_file_id,
      url: file.source_url,
      type: 'image' as const,
    }
  }, [selectedMediaFileId, rawMediaFiles])

  // Get displayed styles
  const displayedStyles = useMemo(() => {
    if (!visualStyles) return []

    // Use recommendations if available
    if (recommendations.visualStyleIds && recommendations.visualStyleIds.length > 0) {
      return recommendations.visualStyleIds
        .map((id) => visualStyles.find((s) => s.id === id))
        .filter((s): s is NonNullable<typeof s> => !!s)
        .slice(0, 3)
    }

    // Fallback to hardcoded defaults
    const defaultNames = DEFAULT_STYLE_NAMES_3
    return defaultNames
      .map((name) => visualStyles.find((s) => s.name === name))
      .filter((s): s is NonNullable<typeof s> => !!s)
  }, [visualStyles, recommendations.visualStyleIds])

  // Get style IDs for the hooks
  const style1Id = displayedStyles[0]?.id ?? null
  const style2Id = displayedStyles[1]?.id ?? null
  const style3Id = displayedStyles[2]?.id ?? null
  const style4Id = displayedStyles[3]?.id ?? null

  // Generate styled images - always call all 4 hooks (React rules)
  const styledImage1 = useStyledImage(selectedSourceImage || undefined, style1Id, brandKitId, true)
  const styledImage2 = useStyledImage(selectedSourceImage || undefined, style2Id, brandKitId, true)
  const styledImage3 = useStyledImage(selectedSourceImage || undefined, style3Id, brandKitId, true)
  const styledImage4 = useStyledImage(selectedSourceImage || undefined, style4Id, brandKitId, true)

  // For mobile single generation: sync additionalStyleId with selectedVisualStyleId
  useEffect(() => {
    if (stepConfig.singleStyleGeneration && selectedVisualStyleId) {
      setAdditionalStyleId(selectedVisualStyleId)
    }
  }, [stepConfig.singleStyleGeneration, selectedVisualStyleId])

  // Additional style selected from modal
  const additionalStyle = visualStyles?.find((s) => s.id === additionalStyleId)
  const styledImageAdditional = useStyledImage(selectedSourceImage || undefined, additionalStyleId, brandKitId, true)

  // Build styled images array
  const styledImages = useMemo((): StyledImageData[] => {
    const styles: StyledImageData[] = []

    // If an additional style was selected, show it first
    if (additionalStyleId && additionalStyle) {
      styles.push({
        id: additionalStyleId,
        name: additionalStyle.name,
        styledUrl: styledImageAdditional.styledUrl,
        isLoading: styledImageAdditional.isLoading,
        isRecommended: false,
      })
    }

    // Add default styles
    const styledImageHooks = [styledImage1, styledImage2, styledImage3, styledImage4]
    for (let i = 0; i < 3 && styles.length < 3; i++) {
      if (displayedStyles[i]) {
        styles.push({
          id: displayedStyles[i].id,
          name: displayedStyles[i].name,
          styledUrl: styledImageHooks[i].styledUrl,
          isLoading: styledImageHooks[i].isLoading,
          isRecommended: i === 0 && !additionalStyleId,
        })
      }
    }

    return styles.slice(0, 3)
  }, [
    displayedStyles,
    additionalStyleId,
    additionalStyle,
    styledImage1,
    styledImage2,
    styledImage3,
    styledImage4,
    styledImageAdditional,
  ])

  // Compute selected style data for font pairing step and preview
  const selectedStyleData = useMemo(() => {
    // For single style generation (mobile): use styledImageAdditional
    if (stepConfig.singleStyleGeneration && selectedVisualStyleId) {
      const style = visualStyles?.find((s) => s.id === selectedVisualStyleId)
      if (style && styledImageAdditional.styledUrl) {
        return {
          id: style.id,
          name: style.name,
          styleUrl: styledImageAdditional.styledUrl,
          cover_image_url: styledImageAdditional.styledUrl,
        }
      }
      // Fallback: return with cover image while generating
      if (style) {
        return {
          id: style.id,
          name: style.name,
          styleUrl: style.cover_image_url,
          cover_image_url: style.cover_image_url,
        }
      }
    }

    // For desktop: use styledImages array
    const selectedStyle = styledImages.find((s) => s.id === selectedVisualStyleId)
    if (!selectedStyle || !selectedStyle.styledUrl) return null
    return {
      id: selectedStyle.id,
      name: selectedStyle.name,
      styleUrl: selectedStyle.styledUrl,
      cover_image_url: selectedStyle.styledUrl,
    }
  }, [
    styledImages,
    selectedVisualStyleId,
    stepConfig.singleStyleGeneration,
    visualStyles,
    styledImageAdditional.styledUrl,
  ])

  // Handler for browse style selection
  const handleBrowseStyleSelect = useCallback(
    (styleId: number) => {
      setSelectedVisualStyleId(styleId)
      const isDefaultStyle = displayedStyles.some((s) => s.id === styleId)
      if (!isDefaultStyle) {
        setAdditionalStyleId(styleId)
        setShouldGenerateAdditionalStyle(true)
      }
    },
    [displayedStyles],
  )

  // Trigger generation for additional style when it changes
  useEffect(() => {
    if (additionalStyleId && shouldGenerateAdditionalStyle) {
      setShouldGenerateAdditionalStyle(false)
      styledImageAdditional.triggerGeneration()
    }
  }, [additionalStyleId, shouldGenerateAdditionalStyle]) // eslint-disable-line react-hooks/exhaustive-deps

  // Track whether we've triggered generation for the current media file
  const [hasTriggeredGeneration, setHasTriggeredGeneration] = useState(false)

  // Reset trigger flag when media file changes
  useEffect(() => {
    setHasTriggeredGeneration(false)
  }, [selectedMediaFileId])

  // Mutations
  const { mutateAsync: updateBrandVisualKit } = useUpdateBrandVisualKit({
    id: brandVisualKitId as number,
  })
  const { mutateAsync: upsertTitleStyle } = useUpsertTextStyle({
    brandVisualKitId: brandVisualKitId as number,
    fontRole: FontRole.Title,
  })
  const { mutateAsync: upsertBodyStyle } = useUpsertTextStyle({
    brandVisualKitId: brandVisualKitId as number,
    fontRole: FontRole.Body,
  })
  const { preferences } = useContentPreferencesFields({ brandKit: brandPlan?.brand_kit })
  const { mutateAsync: updateBrandKit } = useUpdateBrandKit({ id: brandKitId as number })
  const { mutateAsync: updateBrandPlan } = useUpdateBrandPlan({ id: brandPlan?.id as number })

  // Delete custom fonts
  const deleteAllCustomFonts = useCallback(async () => {
    if (customFonts.length === 0) return
    await Promise.all(customFonts.map((font) => deleteFont({ workspaceId: currentWorkspaceId, id: font.id })))
    queryClient.invalidateQueries(['workspaces', currentWorkspaceId, 'fonts', 'list'])
  }, [customFonts, currentWorkspaceId, queryClient])

  // Update content preferences
  const updateContentPreferences = useCallback(async () => {
    const newPreferences = {
      ...preferences,
      apply_visual_style: true,
    }

    if (animatedImagesOptIn) {
      newPreferences.video_sources = ['animated_image']
      newPreferences.video_source_animated_image_weight = 100
      newPreferences.video_source_brand_kit_stock_weight = 0
    } else {
      newPreferences.video_sources = ['brand_kit_stock']
      newPreferences.video_source_animated_image_weight = 0
      newPreferences.video_source_brand_kit_stock_weight = 100
    }

    if (contentPlanValues) {
      newPreferences.featured_media = contentPlanValues.featuredMedia
      newPreferences.featured_media_video_weight = contentPlanValues.mediaWeights.video
      newPreferences.featured_media_image_weight = contentPlanValues.mediaWeights.image
      newPreferences.featured_media_text_weight = contentPlanValues.mediaWeights.text
    }

    await updateBrandKit({ content_preferences: newPreferences })
  }, [preferences, animatedImagesOptIn, contentPlanValues, updateBrandKit])

  // Update content plan
  const updateContentPlan = useCallback(async () => {
    if (!brandPlan?.id || !contentPlanValues) return

    const crosspostContents =
      contentPlanValues.socialPostsCount > 0 ? [{ type: 'post', count: contentPlanValues.socialPostsCount }] : []

    const videoCrosspostContents =
      contentPlanValues.shortFormVideosCount > 0
        ? [{ type: 'post', count: contentPlanValues.shortFormVideosCount }]
        : []

    const seoContents =
      contentPlanValues.blogPostsCount > 0 ? [{ type: 'blog_post', count: contentPlanValues.blogPostsCount }] : []

    const emailContents =
      contentPlanValues.emailsCount > 0 ? [{ type: 'newsletter', count: contentPlanValues.emailsCount }] : []

    await updateBrandPlan({
      tactics: [BrandPlanTactic.Instagram, BrandPlanTactic.InstagramReel],
      crosspost_frequency: 'always',
      crosspost_contents: crosspostContents,
      video_crosspost_contents: videoCrosspostContents,
      seo_contents: seoContents,
      email_contents: emailContents,
      instagram_contents: [
        { type: 'post', count: 0 },
        { type: 'story', count: 0 },
        { type: 'reel', count: 0 },
      ],
      facebook_contents: [{ type: 'post', count: 0 }],
      linkedin_contents: [{ type: 'post', count: 0 }],
      twitter_contents: [{ type: 'post', count: 0 }],
      tiktok_contents: [{ type: 'post', count: 0 }],
      youtube_contents: [{ type: 'short', count: 0 }],
      also_update_schedules: true,
    })
  }, [brandPlan?.id, contentPlanValues, updateBrandPlan])

  // Save styles
  const saveStyles = useCallback(async () => {
    if (!brandVisualKitId) return

    setIsSaving(true)
    const tasks: Promise<unknown>[] = []

    if (selectedVisualStyleId && brandVisualKit) {
      tasks.push(updateBrandVisualKit({ ...brandVisualKit, visual_style_id: selectedVisualStyleId }))
    }

    tasks.push(updateContentPreferences())

    if (contentPlanValues && brandPlan?.id) {
      tasks.push(updateContentPlan())
    }

    if (selectedFontPairingId && fontPairingsData) {
      const selectedPairing = fontPairingsData.find((fp) => fp.id === selectedFontPairingId)
      if (selectedPairing) {
        if (selectedPairing.header_font_config) {
          const headerConfig = selectedPairing.header_font_config
          tasks.push(
            upsertTitleStyle({
              style: {
                font_family: headerConfig.family,
                font_weight: headerConfig.weight as FontStyle['font_weight'],
                letter_spacing: headerConfig.letter_spacing,
                line_height: headerConfig.line_height,
                italic: headerConfig.italic,
                uppercase: headerConfig.uppercase,
              },
              name: headerConfig.family,
            }),
          )
        }
        if (selectedPairing.body_font_config) {
          const bodyConfig = selectedPairing.body_font_config
          tasks.push(
            upsertBodyStyle({
              style: {
                font_family: bodyConfig.family,
                font_weight: bodyConfig.weight as FontStyle['font_weight'],
                letter_spacing: bodyConfig.letter_spacing,
                line_height: bodyConfig.line_height,
                italic: bodyConfig.italic,
                uppercase: bodyConfig.uppercase,
              },
              name: bodyConfig.family,
            }),
          )
        }
      }
    }

    if (tasks.length > 0) {
      await Promise.all(tasks)
      await queryClient.refetchQueries(['workspaces', currentWorkspaceId, 'brandKits'])
      await queryClient.refetchQueries(['workspaces', currentWorkspaceId, 'brandPlans'])
    }

    setIsSaving(false)
  }, [
    brandVisualKitId,
    brandVisualKit,
    selectedVisualStyleId,
    updateBrandVisualKit,
    updateContentPreferences,
    contentPlanValues,
    brandPlan?.id,
    updateContentPlan,
    selectedFontPairingId,
    fontPairingsData,
    upsertTitleStyle,
    upsertBodyStyle,
    queryClient,
    currentWorkspaceId,
  ])

  // Handle regenerate
  const handleRegenerate = useCallback(async () => {
    try {
      await saveStyles()
      await regenerate()
      showToastMessage('Content regeneration started', 'success')
      onClose()
      history.push(generatePath(SCHEDULED_POSTS_PATH, { workspaceId: currentWorkspaceId }))
    } catch (error) {
      showToastMessage('Content regeneration failed. Please try again.', 'error')
    }
  }, [saveStyles, regenerate, onClose, history, currentWorkspaceId])

  // Handle skip regenerate (desktop only)
  const handleSkipRegenerate = useCallback(async () => {
    await saveStyles()
    onClose()
  }, [saveStyles, onClose])

  // Trigger generation for all styled images
  const triggerAllGenerations = useCallback(() => {
    styledImage1.triggerGeneration()
    styledImage2.triggerGeneration()
    styledImage3.triggerGeneration()
    styledImage4.triggerGeneration()
  }, [styledImage1, styledImage2, styledImage3, styledImage4])

  // Trigger generation when on photo-style step and all data is ready
  // This handles the race condition where triggerAllGenerations was called before data was loaded
  useEffect(() => {
    const triggerStepIndex = stepConfig.sequence.indexOf(stepConfig.triggerGenerationOnStep)
    const currentStepIndex = stepConfig.sequence.indexOf(currentStep)
    const isPastTriggerStep = currentStepIndex > triggerStepIndex

    if (
      !stepConfig.singleStyleGeneration &&
      isPastTriggerStep &&
      selectedSourceImage &&
      brandKitId &&
      style1Id &&
      !hasTriggeredGeneration &&
      !styledImage1.styledUrl &&
      !styledImage1.isLoading
    ) {
      setHasTriggeredGeneration(true)
      triggerAllGenerations()
    }
  }, [
    currentStep,
    stepConfig,
    selectedSourceImage,
    brandKitId,
    style1Id,
    hasTriggeredGeneration,
    styledImage1.styledUrl,
    styledImage1.isLoading,
    triggerAllGenerations,
  ])

  // Trigger generation for single selected style (mobile)
  const triggerSingleGeneration = useCallback(() => {
    if (additionalStyleId) {
      styledImageAdditional.triggerGeneration()
    }
  }, [additionalStyleId, styledImageAdditional])

  // Navigation handlers
  const handleNext = useCallback(() => {
    const currentIndex = stepConfig.sequence.indexOf(currentStep)
    if (currentIndex < stepConfig.sequence.length - 1) {
      const nextStep = stepConfig.sequence[currentIndex + 1]

      if (currentStep === 'intro') {
        recommendations.fetch()
      }

      if (currentStep === stepConfig.triggerGenerationOnStep) {
        if (stepConfig.singleStyleGeneration) {
          triggerSingleGeneration()
        } else {
          triggerAllGenerations()
        }
      }

      setCurrentStep(nextStep)
    }
  }, [currentStep, stepConfig, recommendations, triggerAllGenerations, triggerSingleGeneration])

  const handleBack = useCallback(() => {
    const currentIndex = stepConfig.sequence.indexOf(currentStep)
    if (currentIndex > 0) {
      setCurrentStep(stepConfig.sequence[currentIndex - 1])
    }
  }, [currentStep, stepConfig])

  const goToStep = useCallback(
    (step: string) => {
      if (stepConfig.sequence.includes(step)) {
        setCurrentStep(step)
      }
    },
    [stepConfig],
  )

  return {
    currentStep,
    selectedMediaFileId,
    selectedVisualStyleId,
    selectedFontPairingId,
    isSaving,
    additionalStyleId,
    animatedImagesOptIn,
    contentPlanValues,
    setCurrentStep,
    setSelectedMediaFileId,
    setSelectedVisualStyleId,
    setSelectedFontPairingId,
    setAdditionalStyleId,
    setAnimatedImagesOptIn,
    setContentPlanValues,
    displayedStyles,
    styledImages,
    selectedStyleData,
    selectedSourceImage,
    rawMediaFiles,
    plannedSchedules,
    currentScheduleFileIds,
    brandColor,
    brandPlan,
    brandKitId,
    brandKitData,
    brandVisualKit,
    brandVisualKitId,
    fontPairingsData,
    visualStyles,
    recommendations,
    customFonts,
    handleNext,
    handleBack,
    goToStep,
    saveStyles,
    handleRegenerate,
    handleSkipRegenerate,
    deleteAllCustomFonts,
    handleBrowseStyleSelect,
    isRegenerating,
    isGeneratingStyle: stepConfig.singleStyleGeneration ? styledImageAdditional.isLoading : false,
    generatedStyledUrl: styledImageAdditional.styledUrl,
    styledImageHooks: {
      styledImage1,
      styledImage2,
      styledImage3,
      styledImage4,
      styledImageAdditional,
    },
  }
}
